/* eslint-disable react/destructuring-assignment */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React from 'react';
import { Client, HubspotPlan } from '@types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faTimes,
  faEllipsisV,
} from '@fortawesome/free-solid-svg-icons';
import TableDropDown from '@/components/TableDropDown/TableDropDown';
import { MenuAction } from '@/types';
import { Column } from '@/components/Table/Table';
import SelectColorStatus from '@/components/SelectColorStatus';

export interface ClientTableColumnData extends Client {
  financial_delivery_date: Date | undefined;
  latest_pdf_date: string;
  latest_excel_date: string;
  syft: number;
  qbo: boolean;
  active_users: number;
  qbo_transactions: number;
  controllers: string;
  dropdown: string;
  color_status: string;
}

export interface TableClientsColumnsProps {
  actions: MenuAction[];
  showDeliveryDateColumn: boolean | undefined;
  handleFinancialDeliveryDate: (
    financialDate: Date | undefined,
    fileDate: string | undefined
  ) => boolean | undefined;
  handleFinancialDeliveryDateDeal: (
    plan: HubspotPlan | undefined
  ) => React.ReactNode;
  formatDate: (date?: string) => string;
  formatString: (string?: string) => string | null;
  countActiveUsers: (client: Client) => number;
  hasSyftUrl: (client: Client) => number;
  hasQbo: (client: Client) => boolean;
  handleController: (plan: HubspotPlan | undefined) => string | undefined;
}

/**
 * Returns an array of columns for the GenericTable.
 */
export function getTableClientsColumns({
  actions,
  showDeliveryDateColumn,
  handleFinancialDeliveryDate,
  handleFinancialDeliveryDateDeal,
  formatDate,
  formatString,
}: TableClientsColumnsProps): Column<ClientTableColumnData>[] {
  const columns: (Column<ClientTableColumnData> | null)[] = [
    {
      header: 'Company Name',
      accessor: 'client_name',
      sortable: true,
      Cell: (row: ClientTableColumnData) => (
        <div onClick={() => actions[0].action(row)}>{row.client_name}</div>
      ),
    },
    {
      header: 'Status',
      accessor: 'color_status',
      sortable: true,
      Cell: (row: ClientTableColumnData) => (
        <div className='text-center'>
          <SelectColorStatus
            key={`${row.id}-${row.color_status}`}
            clientId={row.id}
            colorStatus={row.color_status || 'gray'}
          />
        </div>
      ),
    },
    showDeliveryDateColumn
      ? {
          header: 'Delivery Date',
          accessor: 'financial_delivery_date',
          sortable: true,
          Cell: (row: ClientTableColumnData) => (
            <div
              onClick={() => actions[0].action(row)}
              className='text-center'
              data-testid='delivery-date-cell-tooltip'
            >
              {handleFinancialDeliveryDateDeal(
                row.hubspot_data?.detailedData?.plan
              )}
            </div>
          ),
        }
      : null,
    {
      header: 'Last PDF',
      accessor: 'latest_pdf_date',
      sortable: true,
      Cell: (row: ClientTableColumnData) => (
        <div
          onClick={() => actions[0].action(row)}
          className={`text-center ${
            row.client_profiles?.latest_pdf_name ? 'tooltip' : ''
          } ${
            handleFinancialDeliveryDate(
              row.hubspot_data?.financial_delivery_date,
              row.client_profiles?.latest_pdf_date
            )
              ? 'text-red-500 font-bold'
              : ''
          }`}
          data-tip={row.client_profiles?.latest_pdf_name}
        >
          {formatDate(row.client_profiles?.latest_pdf_date)}
          {formatString(row.client_profiles?.latest_pdf_name)}
        </div>
      ),
    },
    {
      header: 'Last Excel',
      accessor: 'latest_excel_date',
      sortable: true,
      Cell: (row: ClientTableColumnData) => (
        <div
          onClick={() => actions[0].action(row)}
          className={`text-center ${
            row.client_profiles?.latest_excel_name ? 'tooltip' : ''
          } ${
            handleFinancialDeliveryDate(
              row.hubspot_data?.financial_delivery_date,
              row.client_profiles?.latest_excel_date
            )
              ? 'text-red-500 font-bold'
              : ''
          }`}
          data-tip={row.client_profiles?.latest_excel_name}
          data-testid='latest-excel-cell-tooltip'
        >
          {formatDate(row.client_profiles?.latest_excel_date)}
          {formatString(row.client_profiles?.latest_excel_name)}
        </div>
      ),
    },
    {
      header: 'Syft Url',
      accessor: 'syft',
      sortable: true,
      Cell: (row: ClientTableColumnData) => (
        <div onClick={() => actions[0].action(row)} className='text-center'>
          {row.syft ? (
            <FontAwesomeIcon icon={faCheck} color='green' size='lg' />
          ) : (
            <FontAwesomeIcon icon={faTimes} color='red' size='lg' />
          )}
        </div>
      ),
    },
    {
      header: 'QBO',
      accessor: 'qbo',
      sortable: true,
      Cell: (row: ClientTableColumnData) => (
        <div onClick={() => actions[0].action(row)} className='text-center'>
          {row.qbo ? (
            <FontAwesomeIcon icon={faCheck} color='green' size='lg' />
          ) : (
            <FontAwesomeIcon icon={faTimes} color='red' size='lg' />
          )}
        </div>
      ),
    },
    {
      header: 'Users',
      accessor: 'active_users',
      sortable: true,
      Cell: (row: ClientTableColumnData) => (
        <div onClick={() => actions[0].action(row)} className='text-center'>
          {row.active_users}
        </div>
      ),
    },
    {
      header: 'Transactions',
      accessor: 'qbo_transactions',
      sortable: true,
      Cell: (row: ClientTableColumnData) => (
        <div onClick={() => actions[0].action(row)} className='text-center'>
          {row.qbo_transactions}
        </div>
      ),
    },
    {
      header: 'Controller',
      accessor: 'controllers',
      sortable: true,
      Cell: (row: ClientTableColumnData) => (
        <div
          onClick={() => actions[0].action(row)}
          className='text-center tooltip'
          data-tip={row.controllers}
        >
          {row.controllers}
        </div>
      ),
    },
    {
      header: '',
      accessor: 'dropdown',
      sortable: false,
      Cell: (row: ClientTableColumnData) => (
        <div onClick={(e) => e.stopPropagation()}>
          <TableDropDown
            icon={faEllipsisV}
            options={actions}
            itemToPerformActionOn={row}
            testId='view-client-button'
            id={row.id.toString()}
          />
        </div>
      ),
    },
  ];
  return columns.filter(
    (col): col is Column<ClientTableColumnData> => col !== null
  );
}

export function mapClientToClientTableColumns(
  client: Client,
  {
    countActiveUsers,
    hasSyftUrl,
    hasQbo,
    handleController,
  }: {
    countActiveUsers: (client: Client) => number;
    hasSyftUrl: (client: Client) => number;
    hasQbo: (client: Client) => boolean;
    handleController: (plan: HubspotPlan | undefined) => string | undefined;
  }
): ClientTableColumnData {
  return {
    ...client,
    financial_delivery_date: client.hubspot_data?.financial_delivery_date,
    latest_pdf_date: client.client_profiles?.latest_pdf_date || '',
    latest_excel_date: client.client_profiles?.latest_excel_date || '',
    syft: hasSyftUrl(client),
    qbo: hasQbo(client),
    active_users: countActiveUsers(client),
    qbo_transactions:
      client.qbo_companies?.reduce(
        (acc, obj) => acc + obj.pending_transactions,
        0
      ) || 0,
    controllers:
      handleController(client.hubspot_data?.detailedData?.plan) || '',
    dropdown: '',
    color_status: client.client_profiles?.color_status || 'gray',
  };
}
